import { useState } from 'react'

/* The draft state behind every add/edit sheet.
 *
 * A sheet is mounted for the life of its surface and switched between records
 * by props, so its fields have to be re-seeded whenever it opens on a different
 * record. The obvious way to write that is a useEffect that calls setState —
 * and it works, but it renders the sheet once with the PREVIOUS record's values
 * before the effect corrects them. On a slow frame that stale paint is visible,
 * and `react-hooks/set-state-in-effect` rejects it for exactly this reason.
 *
 * So this adjusts state DURING render instead, which is the pattern React
 * documents for deriving state from props (react.dev, "You Might Not Need an
 * Effect" § Adjusting state when a prop changes). React re-runs the component
 * immediately, before touching the DOM or any child, so the stale values are
 * never painted and no second commit happens.
 *
 * The reset key is the identity of what the sheet is editing: the record's id,
 * or a sentinel for "adding". Reseeding on OPEN rather than on close is
 * deliberate — blanking the fields on close would show the sheet emptying
 * itself during its exit transition.
 */

/* Base UI (`@base-ui/react`): not a component — a state hook for an editable draft. Base UI ships
   no equivalent; its `Form`/`Field` primitives are render-time components, not
   a draft store. */
export function useFormDraft<T>(
  /** Changes whenever the sheet should re-seed — typically `open ? id : null`.
   *  `null` means closed. */
  key: string | null,
  /** Builds the initial draft. Called on every open, never while closed. */
  seed: () => T,
): [
  T,
  React.Dispatch<React.SetStateAction<T>>,
  boolean,
  () => void,
  /** Whether the draft has moved since it was seeded — what an unsaved-changes
   *  guard asks before it lets a sheet close. Appended to the tuple rather than
   *  returned as an object, so every existing call site destructures unchanged. */
  boolean,
] {
  const [draft, setDraft] = useState<T>(seed)
  const [touched, setTouched] = useState(false)
  /* The draft AS SEEDED, kept so "has this changed" is a comparison rather than
     a flag some setter has to remember to raise. A flag would be wrong the
     moment somebody types a character and deletes it again: the sheet would ask
     to discard changes that no longer exist. */
  const [seeded, setSeeded] = useState<T>(draft)
  /* Tracks the last key the draft was SEEDED from, and whether the sheet has
     since closed. Closing alone must not re-seed — the panel is still on screen
     for its exit transition and would be seen emptying itself — but it must
     arm the next open to re-seed even if that open is the SAME record, because
     Cancel discards: reopening a row must never show the abandoned edits. */
  const [seededFrom, setSeededFrom] = useState<string | null>(key)
  const [staleAfterClose, setStaleAfterClose] = useState(false)

  if (key === null) {
    if (!staleAfterClose) setStaleAfterClose(true)
  } else if (key !== seededFrom || staleAfterClose) {
    setSeededFrom(key)
    setStaleAfterClose(false)
    const fresh = seed()
    setDraft(fresh)
    setSeeded(fresh)
    // Validation messages belong to the record that was being edited. Carrying
    // them onto the next one would flag fields the user has not touched yet.
    setTouched(false)
  }

  /* Structural, not referential. A draft holds nested lists — a leg's plates,
     its containers — and every edit to one of those replaces the array, so
     `draft !== seeded` is true the instant anything is re-rendered from a
     spread. What the guard has to know is whether the VALUES differ.
     JSON.stringify is safe here because a draft is a plain literal built by one
     `seed()` and updated by spreads of itself: the key order is fixed by the
     literal, and nothing in one is a Date, a Map or undefined. */
  const dirty = JSON.stringify(draft) !== JSON.stringify(seeded)

  return [draft, setDraft, touched, () => setTouched(true), dirty]
}
